import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { throwError, timer } from 'rxjs';
import { catchError, switchMap, retryWhen, take, concatMap } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { AlertService } from '../service/alert.service';
import { TenantService } from '../service/tenant.service';
import { MaintenanceService } from '../service/maintenance.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const tenantService = inject(TenantService);
  const alertService = inject(AlertService);
  const maintenanceService = inject(MaintenanceService);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);
  const token = authService.getToken();
  
  // Safely access localStorage with browser check
  const lang = (typeof window !== 'undefined' && typeof localStorage !== 'undefined') 
    ? (localStorage.getItem('language') || 'en') 
    : 'en';
  const environment = (typeof window !== 'undefined' && typeof localStorage !== 'undefined') 
    ? (localStorage.getItem('user_environment') || 'production') 
    : 'production';
  
  // Skip alerts for health check requests (handled by maintenance service)
  const isHealthCheck = req.url.includes('/health');

  // Skip token refresh for auth endpoints
  const skipTokenRefresh =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/admin/refresh-token') ||
    req.url.includes('/auth/forgot-password');

  // Public FX APIs must not receive auth/tenant headers (CORS preflight fails).
  const isExternalPublicApi =
    req.url.includes('api.frankfurter.dev') ||
    req.url.includes('api.frankfurter.app');
  if (isExternalPublicApi) {
    return next(req);
  }

  // Note: Removed proactive token refresh - let session service handle timing
  // Only refresh tokens reactively when server returns 401 errors

  // Clone the request to add the Authorization header
  const authReq = token
    ? req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        "x-custom-lang": lang,
        "x-tenant-id": tenantService.getTenant(),
        "X-Environment": environment
      },
    })
    : req.clone({
      setHeaders: {
        "x-custom-lang": lang,
        "x-tenant-id": tenantService.getTenant(),
        "X-Environment": environment
      },
    });

  return next(authReq).pipe(
    // Retry mechanism with exponential backoff for server errors only
    retryWhen(errors =>
      errors.pipe(
        concatMap((error: HttpErrorResponse, index: number) => {
          if ([500, 502, 503, 504].includes(error.status) && index < 3) {
            return timer(Math.pow(2, index) * 1000);
          }
          return throwError(() => error);
        }),
        take(3)
      )
    ),
    catchError((error: HttpErrorResponse) => {
      // Handle 401 errors (Unauthorized)
      // Only attempt token refresh if the original request had a valid token.
      // If there was no token, the user was never authenticated - skip refresh
      // and do NOT trigger handleSessionExpiration (avoids redirect loop).
      if (error.status === 401 && !skipTokenRefresh && isBrowser && token) {
        // Suspended/deleted accounts (e.g. by super admin): show account error, not session expired
        if (authService.isAccountInactiveError(error)) {
          authService.handleSessionExpiration(error);
          return throwError(() => error);
        }

        return authService.refreshToken().pipe(
          switchMap((refreshResponse) => {
            // Token refreshed successfully, retry original request
            const newToken = authService.getToken();
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
                "x-custom-lang": lang,
                "x-tenant-id": tenantService.getTenant(),
                "X-Environment": environment
              },
            });
            return next(retryReq);
          }),
          catchError((refreshError) => {
            // Refresh failed — pass API error so suspension/deletion can be distinguished from expiry
            if (isBrowser) {
              authService.handleSessionExpiration(refreshError);
            }
            return throwError(() => error);
          })
        );
      }

      // Suspended/deleted account may also return 403 without a refresh attempt
      if (
        isBrowser &&
        error.status === 403 &&
        authService.isAccountInactiveError(error) &&
        token
      ) {
        authService.handleSessionExpiration(error);
        return throwError(() => error);
      }

      // For 503 errors, trigger maintenance mode detection (no alert shown)
      if (error.status === 503) {
        maintenanceService.checkMaintenanceStatus();
        return throwError(() => error);
      }

      // Handle network errors during server downtime (only in browser)
      // Skip showing alert if:
      // 1. It's a health check request (maintenance service handles it)
      // 2. Maintenance mode is already active (maintenance page handles it)
      // 3. Running on server (SSR)
      if (
        isBrowser &&
        (error.status === 0 || error.status === 504) && 
        !isHealthCheck && 
        !maintenanceService.isInMaintenanceMode()
      ) {
        // Only show alert for non-health-check requests when not in maintenance mode
        // This prevents duplicate error messages
        // 503 errors are handled above, so no alert needed
        alertService.showAlert(
          'Connection Error',
          'Please try again in a few moments.',
          'warning'
        );
      }

      // Handle other 401 errors from auth endpoints (only in browser)
      if (isBrowser && error.status === 401 && req.url.includes('/auth/')) {
        alertService.showAlert(
          'Authentication Error',
          'Please check your credentials and try again.',
          'error'
        );
      }

      return throwError(() => error);
    })
  );
};
